/**
 * EPL PDF Generator - Generador de reportes ejecutivos profesionales
 * Reutilizable para admin y estudiantes
 */

import jsPDF from 'jspdf';

/**
 * Genera un PDF ejecutivo profesional con el diseño EPL
 * @param {Object} data - Datos del reporte
 * @param {string} data.labName - Nombre del laboratorio
 * @param {string} data.userName - Nombre del estudiante
 * @param {string} data.userEmail - Email del estudiante
 * @param {string} data.systemCard - Sistema EPL (innovafast, innovatech, etc.)
 * @param {Date|string} data.completedAt - Fecha de completación
 * @param {Object} data.aiResults - Resultados de IA {score, feedback, recommendations}
 * @param {Array} data.responses - Respuestas del estudiante [{fieldLabel, fieldValue}]
 * @param {number} data.attempts - Intentos realizados
 * @param {number} data.timeSpent - Tiempo empleado en segundos (opcional)
 */
export const generateEPLExecutiveReport = async (data) => {
  const {
    labName = 'Laboratorio EPL',
    userName = 'N/A',
    userEmail = 'N/A',
    systemCard = 'innovafast',
    completedAt,
    aiResults = {},
    responses = [],
    attempts = 0,
    timeSpent = 0
  } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 15;

  // ========== HEADER PROFESIONAL CON LOGO EPL ==========
  doc.setFillColor(44, 82, 130);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  // Logo isotipo EduTech - Cargar imagen
  try {
    const logoImg = await loadImage('/assets/isotipoedutech.png');
    doc.addImage(logoImg, 'PNG', 12, 8, 20, 20);
  } catch (error) {
    console.warn('No se pudo cargar el logo, usando fallback', error);
    // Fallback: cuadrado con "E" estilizado
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(2);
    doc.circle(22, 18, 10, 'S');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('E', 19, 21);
  }
  
  // Título principal en blanco
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('EDUTECH PULSE LAB', 37, 19);
  
  // Subtítulo
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Reporte Ejecutivo de Evaluación', 37, 25);
  
  yPosition = 45;

  // ========== TÍTULO DEL LABORATORIO ==========
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(44, 82, 130);
  const titleX = (pageWidth - doc.getTextWidth(labName)) / 2;
  doc.text(labName, titleX, yPosition);
  
  // Línea decorativa
  doc.setDrawColor(44, 82, 130);
  doc.setLineWidth(0.5);
  doc.line(15, yPosition + 3, pageWidth - 15, yPosition + 3);
  
  yPosition += 15;

  // ========== INFORMACIÓN GENERAL (CARD STYLE) ==========
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(15, yPosition, pageWidth - 30, 30, 2, 2, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text('Estudiante:', 20, yPosition + 7);
  doc.text('Email:', 20, yPosition + 12);
  doc.text('Sistema:', 20, yPosition + 17);
  doc.text('Fecha:', 20, yPosition + 22);
  doc.text('Intentos:', 20, yPosition + 27);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  doc.text(userName, 65, yPosition + 7);
  doc.text(userEmail, 65, yPosition + 12);
  doc.text(getSystemLabel(systemCard), 65, yPosition + 17);
  doc.text(formatDate(completedAt), 65, yPosition + 22);
  doc.text(`${attempts}/3`, 65, yPosition + 27);
  
  yPosition += 40;

  // ========== SECCIÓN DE RESPUESTAS (si existen) ==========
  if (responses && responses.length > 0) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 82, 130);
    doc.text('PREGUNTAS Y RESPUESTAS', 15, yPosition);
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(15, yPosition + 2, pageWidth - 15, yPosition + 2);
    
    yPosition += 10;

    responses.forEach((item, index) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Número de pregunta
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 82, 130);
      doc.text(`${index + 1}.`, 15, yPosition);
      
      // Pregunta
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      const splitQuestion = doc.splitTextToSize(item.fieldLabel || 'Pregunta', pageWidth - 40);
      doc.text(splitQuestion, 23, yPosition);
      yPosition += (splitQuestion.length * 4) + 3;

      // Detectar si es una matriz para renderizar como tabla
      const isMatrix = item.fieldValue && 
                      typeof item.fieldValue === 'object' && 
                      !Array.isArray(item.fieldValue) &&
                      Object.keys(item.fieldValue).some(k => k.includes('-'));

      if (isMatrix && item.fieldConfig) {
        // Renderizar como tabla
        yPosition = renderMatrixAsTable(doc, item.fieldValue, item.fieldConfig, yPosition, pageWidth);
      } else {
        // Respuesta normal con fondo gris
        const answerText = formatValue(item.fieldValue, item.fieldConfig);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(40, 40, 40);
        
        // Dividir por saltos de línea primero, luego por ancho
        const lines = answerText.split('\n');
        const allSplitLines = [];
        lines.forEach(line => {
          const split = doc.splitTextToSize(line || ' ', pageWidth - 40);
          allSplitLines.push(...split);
        });
        
        const answerHeight = allSplitLines.length * 5 + 3; // 5 en vez de 4 para mejor espaciado
        
        // Verificar si necesita nueva página
        if (yPosition + answerHeight > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(22, yPosition - 2, pageWidth - 37, answerHeight, 1, 1, 'F');
        
        allSplitLines.forEach((line, idx) => {
          doc.text(line, 24, yPosition + 1 + (idx * 5));
        });
        
        yPosition += answerHeight + 5;
      }
    });

    yPosition += 5;
  }

  // ========== SECCIÓN DE FEEDBACK IA ==========
  if (yPosition > 180) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(44, 82, 130);
  doc.text('FEEDBACK DE INTELIGENCIA ARTIFICIAL', 15, yPosition);
  
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(15, yPosition + 2, pageWidth - 15, yPosition + 2);
  
  yPosition += 10;

  // SCORE DESTACADO
  if (aiResults.score !== null && aiResults.score !== undefined) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('PUNTUACIÓN', 15, yPosition);
    
    doc.setFontSize(36);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 82, 130);
    const scoreText = String(aiResults.score);
    const scoreWidth = doc.getTextWidth(scoreText);
    doc.text(scoreText, 15, yPosition + 12);
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text('/100', 15 + scoreWidth + 2, yPosition + 12);
    
    yPosition += 22;
  }
  
  // Feedback text
  const feedbackText = aiResults.feedback || 'Evaluación completada exitosamente.';
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  
  const splitFeedback = doc.splitTextToSize(feedbackText, pageWidth - 30);
  splitFeedback.forEach((line, index) => {
    if (yPosition + (index * 7) > 270) {
      doc.addPage();
      yPosition = 20;
    }
    doc.text(line, 15, yPosition + (index * 7));
  });
  yPosition += splitFeedback.length * 7 + 12;
  
  // Recomendaciones
  if (aiResults.recommendations && aiResults.recommendations.length > 0) {
    if (yPosition > 240) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 82, 130);
    doc.text('RECOMENDACIONES', 15, yPosition);
    yPosition += 8;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    
    aiResults.recommendations.forEach((rec, i) => {
      if (yPosition > 260) {
        doc.addPage();
        yPosition = 20;
      }
      const recText = `${i + 1}. ${rec}`;
      const splitRec = doc.splitTextToSize(recText, pageWidth - 40);
      splitRec.forEach((line, index) => {
        doc.text(line, 20, yPosition + (index * 5));
      });
      yPosition += splitRec.length * 5 + 3;
    });
  }

  // ========== FOOTER MINIMALISTA ==========
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const footerY = pageHeight - 15;
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(15, footerY - 5, pageWidth - 15, footerY - 5);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text('EduTech Pulse Lab', 15, footerY);
    
    const timestamp = new Date().toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const pageInfo = `Página ${i} de ${totalPages} | ${timestamp}`;
    const pageInfoWidth = doc.getTextWidth(pageInfo);
    doc.text(pageInfo, pageWidth - 15 - pageInfoWidth, footerY);
  }

  // Guardar PDF
  const fileName = `EPL_${userName.replace(/\s+/g, '_')}_${labName.substring(0, 30).replace(/\s+/g, '_')}.pdf`;
  doc.save(fileName);
};

// ========== FUNCIONES AUXILIARES ==========

// Renderizar matriz como tabla visual en PDF
function renderMatrixAsTable(doc, matrixData, fieldConfig, startY, pageWidth) {
  console.log('📊 Renderizando matriz:', { matrixData, fieldConfig });
  
  let rowLabels = fieldConfig.rowLabels || [];
  let colLabels = fieldConfig.colLabels || [];
  
  // Si no hay colLabels, detectar del matrixData
  if (colLabels.length === 0) {
    const keys = Object.keys(matrixData);
    if (keys.length > 0) {
      const maxCol = Math.max(...keys.map(k => parseInt(k.split('-')[1])));
      colLabels = Array.from({ length: maxCol + 1 }, (_, i) => `Opción ${i + 1}`);
    }
  }
  
  // Si no hay rowLabels, detectar del matrixData
  if (rowLabels.length === 0) {
    const keys = Object.keys(matrixData);
    if (keys.length > 0) {
      const maxRow = Math.max(...keys.map(k => parseInt(k.split('-')[0])));
      rowLabels = Array.from({ length: maxRow + 1 }, (_, i) => `Fila ${i + 1}`);
    }
  }
  
  console.log('🏷️ Labels finales:', { rowLabels, colLabels });
  
  // Si aún no hay labels, no renderizar como tabla
  if (rowLabels.length === 0 || colLabels.length === 0) {
    console.warn('⚠️ No se pudieron generar labels, saltando renderizado de tabla');
    return startY;
  }
  
  // Configuración de tabla
  const margin = 22;
  const tableWidth = pageWidth - margin * 2;
  const cellHeight = 8;
  const headerHeight = 10;
  
  // Calcular altura total de la tabla
  const totalTableHeight = headerHeight + (rowLabels.length * cellHeight) + 5;
  const pageHeight = doc.internal.pageSize.height;
  
  // Si no cabe, nueva página
  if (startY + totalTableHeight > pageHeight - 20) {
    doc.addPage();
    startY = 20;
  }
  
  // Calcular ancho de columnas
  const numCols = colLabels.length;
  const labelColWidth = tableWidth * 0.4; // 40% para labels de filas
  const dataColWidth = (tableWidth - labelColWidth) / numCols;
  
  let yPos = startY;
  
  // Header de la tabla (nombres de columnas)
  doc.setFillColor(44, 82, 130);
  doc.rect(margin, yPos, tableWidth, headerHeight, 'F');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  
  // Headers de columnas con salto de línea
  colLabels.forEach((colLabel, colIndex) => {
    const colX = margin + labelColWidth + (colIndex * dataColWidth);
    const splitLabel = doc.splitTextToSize(colLabel, dataColWidth - 4);
    
    // Si el texto es muy largo, reducir tamaño de fuente
    if (splitLabel.length > 2) {
      doc.setFontSize(6);
    }
    
    // Centrar verticalmente las líneas
    const lineHeight = 3;
    const startY = yPos + (headerHeight - (splitLabel.length * lineHeight)) / 2 + 3;
    
    splitLabel.slice(0, 3).forEach((line, lineIdx) => {
      doc.text(line, colX + 2, startY + (lineIdx * lineHeight));
    });
    
    doc.setFontSize(7); // Restaurar tamaño
  });
  
  yPos += headerHeight;
  
  // Filas de datos
  rowLabels.forEach((rowLabel, rowIndex) => {
    // Celda de label de fila (con fondo gris claro)
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos, labelColWidth, cellHeight, 'F');
    
    // Borde
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.rect(margin, yPos, labelColWidth, cellHeight);
    
    // Texto del label con salto de línea
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    const splitRowLabel = doc.splitTextToSize(rowLabel, labelColWidth - 4);
    
    // Si el texto es muy largo, reducir tamaño
    if (splitRowLabel.length > 2) {
      doc.setFontSize(6);
    }
    
    // Renderizar líneas
    const lineHeight = 3;
    const startYRow = yPos + (cellHeight - (splitRowLabel.length * lineHeight)) / 2 + 2.5;
    
    splitRowLabel.slice(0, 2).forEach((line, lineIdx) => {
      doc.text(line, margin + 2, startYRow + (lineIdx * lineHeight));
    });
    
    doc.setFontSize(7); // Restaurar tamaño
    
    // Celdas de datos
    colLabels.forEach((colLabel, colIndex) => {
      const cellX = margin + labelColWidth + (colIndex * dataColWidth);
      const key = `${rowIndex}-${colIndex}`;
      const value = matrixData[key] || '';
      
      console.log(`📍 Celda [${rowIndex},${colIndex}] key="${key}" value="${value}"`);
      
      // Fondo blanco para celdas de datos
      doc.setFillColor(255, 255, 255);
      doc.rect(cellX, yPos, dataColWidth, cellHeight, 'F');
      
      // Borde
      doc.setDrawColor(200, 200, 200);
      doc.rect(cellX, yPos, dataColWidth, cellHeight);
      
      // Valor
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      doc.text(String(value), cellX + 2, yPos + 5);
    });
    
    yPos += cellHeight;
  });
  
  return yPos + 5; // Retornar nueva posición Y con espacio adicional
}

function getSystemLabel(systemCard) {
  const badges = {
    innovafast: 'InnovaFast',
    innovatech: 'InnovaTech',
    transforma360: 'Transforma360',
    gestion_crisis: 'Gestión de Crisis',
    dinamicas_equipo: 'Dinámicas de Equipo',
    comunicacion_estrategica: 'Comunicación Estratégica',
    dss_builder: 'DSS Builder',
    analisis_riesgo: 'Análisis de Riesgo',
    planificacion_estrategica: 'Planificación Estratégica'
  };
  return badges[systemCard] || 'EPL System';
}

function formatDate(date) {
  if (!date) return new Date().toLocaleDateString('es-ES');
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatValue(value, fieldConfig = null) {
  if (value === null || value === undefined) return 'Sin respuesta';
  
  if (typeof value === 'object') {
    // Detectar si es una matriz con formato "0-0", "1-1", etc.
    if (!Array.isArray(value)) {
      const keys = Object.keys(value);
      const isMatrix = keys.length > 0 && keys[0].includes('-');
      
      if (isMatrix && fieldConfig) {
        // Usar etiquetas de filas y columnas del fieldConfig
        const rowLabels = fieldConfig.rowLabels || [];
        const colLabels = fieldConfig.colLabels || [];
        
        const entries = Object.entries(value);
        const formattedRows = entries.map(([key, val]) => {
          const [row, col] = key.split('-').map(Number);
          const rowLabel = rowLabels[row] || `Fila ${row + 1}`;
          const colLabel = colLabels[col] || `Columna ${col + 1}`;
          return `${rowLabel} - ${colLabel}: ${val}`;
        });
        return formattedRows.join('\n');
      } else if (isMatrix) {
        // Fallback sin etiquetas
        const entries = Object.entries(value);
        const formattedRows = entries.map(([key, val]) => {
          const [row, col] = key.split('-');
          return `Fila ${parseInt(row) + 1}, Columna ${parseInt(col) + 1}: ${val}`;
        });
        return formattedRows.join('\n');
      }
    }
    
    // Para arrays normales, formatear como texto legible
    if (Array.isArray(value)) {
      return value.map((row, i) => 
        Array.isArray(row) ? `Fila ${i + 1}: ${row.join(', ')}` : row
      ).join('\n');
    }
    
    // Para objetos simples, intentar formato clave: valor
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');
  }
  
  return String(value);
}

// Función helper para cargar imágenes de forma asíncrona
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
